import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { MerchantNav } from "../components/MerchantNav";
import {
  hasMerchantCredential,
  merchantFetch,
  type MerchantRole,
} from "../lib/merchantAuth";

/**
 * Phase 40 — Owner-only team management page. Lists every tenant user
 * (Owner + Managers + Staff), lets the owner add a new Manager / Staff,
 * change a user's role, suspend / reactivate them.
 *
 * The Owner row itself is read-only (cannot demote, cannot suspend).
 * STAFF + MANAGER reaching this URL get redirected to the Dashboard
 * (the server enforces with 403; the client redirect is just a UX
 * smoothing).
 */

interface TenantUserSummary {
  id: string;
  tenantId: string;
  email: string;
  role: MerchantRole;
  status: "ACTIVE" | "SUSPENDED";
  mustSetPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function MerchantTeamPage() {
  if (!hasMerchantCredential()) return <Redirect to="/merchant/login" />;

  const [users, setUsers] = useState<TenantUserSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-user form
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newRole, setNewRole] = useState<"MANAGER" | "STAFF">("STAFF");

  async function load() {
    const r = await merchantFetch<{ users: TenantUserSummary[] }>(
      "/api/v1/tenant/users",
    );
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    setErr(null);
    setUsers(r.data.users);
  }

  useEffect(() => {
    void load();
  }, []);

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!newEmail.trim() || newPwd.length < 10) {
      setErr("Email is required and the temporary password must be at least 10 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await merchantFetch<{ user: TenantUserSummary }>(
        "/api/v1/tenant/users",
        {
          method: "POST",
          body: JSON.stringify({
            email: newEmail.trim(),
            initialPassword: newPwd,
            role: newRole,
          }),
        },
      );
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      setNewEmail("");
      setNewPwd("");
      setNewRole("STAFF");
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(userId: string, role: "MANAGER" | "STAFF") {
    const r = await merchantFetch(`/api/v1/tenant/users/${userId}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    });
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    await load();
  }

  async function changeStatus(
    userId: string,
    status: "ACTIVE" | "SUSPENDED",
  ) {
    const r = await merchantFetch(`/api/v1/tenant/users/${userId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    await load();
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-6">
      <MerchantNav current="team" />
      <h1 className="mt-6 text-2xl font-semibold text-navy">Team</h1>
      <p className="mt-1 text-sm text-ink-dark">
        Manage the people who can sign in to your tenant. Owners see and
        change everything. Managers see the dashboard and inbox. Staff
        only see redeem, scan, and today&rsquo;s transactions.
      </p>

      {err && (
        <p className="mt-4 rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </p>
      )}

      <section className="mt-6 rounded-card border border-ink-light/40 bg-white p-4">
        <h2 className="text-sm font-semibold text-navy">Add a teammate</h2>
        <form onSubmit={addUser} className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input
            type="email"
            placeholder="email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="input sm:col-span-2"
          />
          <input
            type="text"
            placeholder="temporary password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            className="input font-mono text-sm"
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as "MANAGER" | "STAFF")}
            className="input"
          >
            <option value="STAFF">Staff</option>
            <option value="MANAGER">Manager</option>
          </select>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary sm:col-span-4"
          >
            {busy ? "Adding…" : "Add user"}
          </button>
        </form>
        <p className="mt-2 text-xs text-ink-dark">
          The new user will be required to set their own password on first sign in.
        </p>
      </section>

      <section className="mt-6 overflow-hidden rounded-card border border-ink-light/40 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ink-light/10 text-left text-xs uppercase tracking-wide text-ink-dark">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-ink-dark">
                  Loading…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-ink-dark">
                  No users yet.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-ink-light/20">
                  <td className="px-3 py-2">
                    <div>{u.email}</div>
                    {u.mustSetPassword && (
                      <div className="text-xs text-amber-600">Must set password</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.role === "OWNER" ? (
                      <span className="font-semibold">Owner</span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) =>
                          changeRole(u.id, e.target.value as "MANAGER" | "STAFF")
                        }
                        className="input py-1 text-sm"
                      >
                        <option value="MANAGER">Manager</option>
                        <option value="STAFF">Staff</option>
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {u.status === "ACTIVE" ? (
                      <span className="text-emerald-700">Active</span>
                    ) : (
                      <span className="text-red-700">Suspended</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-dark">
                    {u.lastLoginAt
                      ? new Date(u.lastLoginAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {u.role !== "OWNER" && (
                      u.status === "ACTIVE" ? (
                        <button
                          type="button"
                          onClick={() => changeStatus(u.id, "SUSPENDED")}
                          className="text-sm text-red-700 hover:underline"
                        >
                          Suspend
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => changeStatus(u.id, "ACTIVE")}
                          className="text-sm text-emerald-700 hover:underline"
                        >
                          Reactivate
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </main>
  );
}
